import gql from "graphql-tag";
import {useMemo} from "react";

import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {useGuideState} from "@khan/guide-state";

import type {configSupportsImageInputType} from "./__graphql-types__/index.ts";

export const SUPPORTS_IMAGE_INPUT_QUERY: GqlOperation<
    configSupportsImageInputType["response"],
    configSupportsImageInputType["variables"]
> = gqlOp(gql`
    query configSupportsImageInput($configName: String!) {
        aiGuideConfigSupportsImageInput(configName: $configName) {
            supportsImageInput
        }
    }
`);

/**
 * A hook that takes the current config name from the guide state
 * and queries the backend to see if image input is supported.
 * Returns false if there is no config name or if the query fails.
 */
export function useConfigSupportsImageInput(): boolean {
    const configName = useGuideState("selectedBotName");

    // Memoize the variables to prevent unnecessary re-renders
    const variables = useMemo(
        () => ({
            configName: configName || "",
        }),
        [configName],
    );

    const [result] = useQuery(SUPPORTS_IMAGE_INPUT_QUERY, {
        variables,
        skip: !configName,
    });

    // Return false if the query was skipped or failed
    if (result?.status !== "success") {
        return false;
    }

    // Wrap in a boolean to handle nullish values
    return Boolean(
        result.data?.aiGuideConfigSupportsImageInput?.supportsImageInput,
    );
}
